import { getActiveTraceHttpRequestHeaders } from '@atlaskit/react-ufo/experience-trace-id-context';

import { fetchBase } from '@atlassian/pillar/runtime/fetch';

import { fg } from '@confluence/feature-gating';
import { appendHeader, cfetch, networkTracker, shouldUseGetMethod } from '@confluence/network';
import { WaterfallMeasures } from '@confluence/action-measures/entry-points/WaterfallMeasures.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { getAtlAttributionHeader } from '@confluence/query-preloader-tools/entry-points/getAtlAttributionHeader.ts';
import { getQueryName } from '@confluence/query-preloader-tools/entry-points/getQueryName.ts';

import {
	createExperienceAwareFetch,
	isExperienceAwareFetchAlwaysEnabled,
} from './createExperienceAwareFetch.ts';

const experienceAwareFetch = createExperienceAwareFetch();

export const AGG_SLOW_URI = '/gateway/api/graphql/slow';

const sendAnalyticsEvent = (
	headers: Record<string, string>,
	regionHint: string,
	isSSR: boolean,
) => {
	void import(
		/* webpackChunkName: "loadable-confluenceanalytics-web-client" */ '@confluence/analytics-web-client'
	).then(({ getAnalyticsWebClient }) => {
		void getAnalyticsWebClient().then((analyticsWebClient) => {
			analyticsWebClient.sendUIEvent({
				source: 'createWrappedFetch',
				action: 'observed',
				actionSubject: 'graphqlRegionHintHeaders',
				actionSubjectId: 'graphqlHeadersSent',
				attributes: {
					headers,
					regionHint,
					isSSR,
				},
			});
		});
	});
};

// appends the operation names as query params, and
// includes them as headers as well
export const createWrappedFetch = (isForAggOnly: boolean) => {
	return (uri: string, options: RequestInit) => {
		const payload: Array<{
			operationName: string;
			query: string;
			variables: { [key: string]: any };
		}> = [].concat(typeof options.body === 'string' ? JSON.parse(options.body) : []); // concat normalizes into an array

		// Extract requestId from headers (set by RequestIdLink)
		// The requestId is available here for use in the fetch function
		const headers = (options.headers || {}) as Record<string, string>;
		const requestId = headers['X-Request-Id'] || headers['x-request-id'];

		// Convert to GET if requestId is present and shouldUseGetMethod is true
		// When requestId is present, assume payload array length is 1
		let shouldUseGet = false;
		let fallbackReason: string | undefined;
		if (requestId) {
			const { isGetRequest, reason } = shouldUseGetMethod({
				url: uri,
				variables: payload[0].variables,
				headers: options.headers,
				payload,
			});
			shouldUseGet = isGetRequest;
			fallbackReason = reason;
		}

		const operationNames = payload.map(({ operationName, variables }) =>
			getQueryName(operationName, variables),
		);

		const queriesExcludedFromAggMigration =
			StatsigConfigurations.getStringList('cc-agg-migration-excluded-queries', []) || [];

		const isExcludedFromAggMigration = operationNames.some((name) =>
			queriesExcludedFromAggMigration.includes(name),
		);
		const isUsingAggImplicitly =
			!isExcludedFromAggMigration && fg('cc-agg-migration-use-agg-endpoint');

		if (!isForAggOnly) {
			if (isUsingAggImplicitly) {
				uri = shouldUseGet ? `/pq/${requestId}` : AGG_SLOW_URI;
			} else {
				// On client side the uri is /cgraphql
				// However when running on the server side we want to dynamic change the URI
				// So we can route the request to the graphql deployment that closer to the data.
				// AGG host is redirected by Tesseract so we don't need to change the URI
				uri = window.GLOBAL_APOLLO_CLIENT_URI || uri;
			}
		}

		// Track individual operations for frontend
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR && typeof window !== 'undefined') {
			networkTracker.trackGraphQLOperations(operationNames);
		}

		// pass as headers for rate-limiting compatibility
		const operationNamesList = operationNames.join(',');
		if (!options.headers) {
			options.headers = {};
		}

		// AGG batch queries require /batch to be added to the uri and an additional header
		if (isUsingAggImplicitly) {
			appendHeader(options.headers, 'X-ExperimentalApi', 'confluence-agg-beta');
			if (payload.length === 1) {
				options.body = JSON.stringify(payload[0]);
			} else if (payload.length > 1) {
				appendHeader(options.headers, 'X-ExperimentalApi', 'BatchOperations');
				uri = `${uri}/batch`;
			}
		}

		appendHeader(options.headers, 'X-APOLLO-OPERATION-NAME', operationNamesList);

		const shouldApplyAggHeaders = fg('cc-agg-migration-use-agg-endpoint')
			? !isExcludedFromAggMigration
			: isForAggOnly;

		if (shouldApplyAggHeaders) {
			const header = getAtlAttributionHeader();
			appendHeader(options.headers, 'atl-attribution', header['atl-attribution']);
		}

		if (shouldApplyAggHeaders || process.env.BIFROST_TEMPLATE_BUILD === 'true') {
			// These headers are explicitly forwarded to cc-graphql in cc-frontend-ssr, but we need to apply them here for Bifrost requests to cc-graphql.
			// We forward the same headers for AGG and cc-graphql requests
			const { aggHeaders } = getSSRRenderInputs();
			if (aggHeaders) {
				Object.entries(aggHeaders).forEach(([header, val]) => {
					if (val) {
						appendHeader(options.headers!, header, val);
					}
				});
			}
		}

		// Note: this may overrwrite a header value from above in SSR context, but this ensures
		// it's still applied in SPA context
		if (window['GLOBAL_AGG_REGION_STEERING_HINT']) {
			appendHeader(
				options.headers,
				'Atl-Edge-Region-Hint',
				window['GLOBAL_AGG_REGION_STEERING_HINT']!,
			);
		}

		appendHeader(options.headers, 'ATL-AGG-Confluence-MutationError-Compat', 'false');

		options.referrerPolicy = options.referrerPolicy || 'same-origin';

		// Tesseract instruments the outgoing http requests automatically, no need to pass tracing headers when running in SSR.
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			// add experience tracing headers

			const tracingHeaders = getActiveTraceHttpRequestHeaders('' /* url, not used */);
			options.headers = Object.assign(options.headers, tracingHeaders);
		}

		// At this point the query has lost the structure and formatted back to string
		// However it is different in experimental queries. The AST is always accessible
		// Refer to next/packages/graphql/src/links/ExperimentalLink/schemaLink.ts
		const executeFetch = () => {
			// Prepare options for GET conversion if needed
			let finalOptions = { ...options };

			if (fg('show_region_hint_event')) {
				sendAnalyticsEvent(
					finalOptions.headers as Record<string, string>,
					window['GLOBAL_AGG_REGION_STEERING_HINT'] || '',
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
					Boolean(process.env.REACT_SSR),
				);
			}
			if (shouldUseGet) {
				// Convert POST to GET by moving variables to URL params
				const params = new URLSearchParams();
				params.append('variables', JSON.stringify(payload[0].variables));

				finalOptions = {
					...finalOptions,
					method: 'GET',
				};
				// Remove body for GET requests
				delete finalOptions.body;
			}

			// Add fallback reason header when we're not able to use GET
			if (!shouldUseGet && fallbackReason) {
				appendHeader(
					finalOptions.headers as Record<string, string>,
					'X-Request-Fallback-To-Post-Reason',
					fallbackReason,
				);
			}

			if (process.env.BIFROST_TEMPLATE_BUILD === 'true') {
				/**
				 * Request route through AP when x-ssr-asap-audience is provided.
				 */
				const headers = (finalOptions.headers || {}) as Record<string, string>;
				const requestAgg = isForAggOnly || isUsingAggImplicitly;
				const { monolithAwsRegion } = getSSRRenderInputs();

				if (process.env.PILLAR_LOCAL_DEVELOPMENT !== 'true') {
					if (!requestAgg) {
						// We don't set the header for agg because it is handled specially
						appendHeader(headers, 'x-ssr-asap-audience', 'cc-graphql');
						appendHeader(headers, 'x-ssr-service-name', 'cc-graphql');
						if (monolithAwsRegion) {
							appendHeader(headers, 'x-ssr-service-region', monolithAwsRegion);
						}
					}
				}

				let baseUrl = '/cgraphql';
				if (requestAgg) {
					baseUrl = shouldUseGet ? `/pq/${requestId}` : AGG_SLOW_URI;
				}
				if (isUsingAggImplicitly && payload.length > 1) {
					baseUrl = `${baseUrl}/batch`;
				}

				const urlWithQuery = shouldUseGet
					? `${baseUrl}?q=${operationNamesList}&variables=${encodeURIComponent(JSON.stringify(payload[0].variables))}`
					: `${baseUrl}?q=${operationNamesList}`;

				return fetchBase({
					name: requestAgg ? 'fetchAgg' : 'fetchCGraphql',
					url: urlWithQuery,
					headers,
					options: finalOptions,
				});
			}

			return (
				// item might not always have a query property - as in the case of persisted queries
				(isExperienceAwareFetchAlwaysEnabled() ? experienceAwareFetch : cfetch)(
					shouldUseGet
						? `${uri}?q=${operationNamesList}&variables=${encodeURIComponent(JSON.stringify(payload[0].variables))}`
						: `${uri}?q=${operationNamesList}`,
					finalOptions,
				)
			);
		};

		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		const markingPrefix = process.env.REACT_SSR
			? `ssr-app/prepare/preloadQuery/fetch:`
			: 'wf/client-graphql/';
		const markingNames = operationNames.map((name) => {
			// Note: whole batch goes to cc-graphql, if one query is excluded w/in a batch
			name = shouldApplyAggHeaders ? `agg_${name}` : name;
			return `${markingPrefix}${name?.replace(/[^\-\.\w]/g, '_')}`;
		});
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			// Track individual GraphQL operations count for SSR
			SSRMeasures.operationsCountIncr(operationNames.length);

			// Wrap executeFetch to track GraphQL errors
			const errorTrackingExecuteFetch = async () => {
				try {
					return await executeFetch();
				} catch (error) {
					// Track GraphQL errors during SSR
					SSRMeasures.errorCountIncr(operationNames.length);
					throw error;
				}
			};

			return SSRMeasures.run(markingNames, errorTrackingExecuteFetch);
		}
		// Ignore batch queries because they are not deterministic. Don't make sense to show on performance waterfall.
		return operationNames.length > 1
			? executeFetch()
			: WaterfallMeasures.run(markingNames, executeFetch);
	};
};
